import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, MessageCircle } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { useEtatGlobal } from "../EtatGlobal.js";
import DiscussionNotifRow from "./DiscussionNotifRow";
import { INK, INK_3, LINE } from "../Bureau/bureauTokens";

// Panneau de notifications Discussion — glisse depuis la droite (aucun
// composant "drawer" n'existait encore dans le repo, seul src/components/ui/Dialog.js
// propose des modales centrées). z-[100000] : reste au-dessus de Dialog.js
// (z-[99999]) si une confirmation devait s'ouvrir par-dessus.
function DiscussionNotifDrawer({ onClose, onOpenConversation }) {
  const { users, fetchDiscussionUnreadCount } = useEtatGlobal();
  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingIds, setMarkingIds] = useState(() => new Set());

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    httpClient
      .get("/api/discussion/conversations")
      .then(({ data }) => setItems((data.data || []).filter((c) => c.unreadCount > 0)))
      .catch((err) => console.error("Erreur chargement notifications Discussion :", err))
      .finally(() => setLoading(false));
  }, []);

  const usersById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const markRead = (conversationId) => {
    setMarkingIds((prev) => new Set(prev).add(conversationId));
    httpClient
      .post(`/api/discussion/conversations/${conversationId}/read`)
      .then(() => {
        setItems((prev) => prev.filter((c) => c.id !== conversationId));
        fetchDiscussionUnreadCount();
      })
      .catch((err) => console.error("Erreur marquage lu Discussion :", err))
      .finally(() =>
        setMarkingIds((prev) => {
          const next = new Set(prev);
          next.delete(conversationId);
          return next;
        }),
      );
  };

  return createPortal(
    <div className="fixed inset-0 z-[100000]">
      <div
        className="absolute inset-0 bg-black/40 transition-opacity"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={close}
      />
      <div
        className="absolute top-0 right-0 h-full flex flex-col transition-transform duration-200"
        style={{
          width: 380,
          maxWidth: "90vw",
          background: "#fff",
          boxShadow: "-12px 0 32px rgba(0,0,0,.16)",
          transform: visible ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-4" style={{ borderBottom: `1px solid ${LINE}` }}>
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: INK }}>
            <MessageCircle size={16} /> Notifications
          </div>
          <button onClick={close} className="flex items-center justify-center" style={{ color: INK_3 }}>
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-1.5">
          {items.map((c) => (
            <DiscussionNotifRow
              key={c.id}
              otherUser={usersById[c.otherUserId]}
              lastMessage={c.lastMessage}
              unreadCount={c.unreadCount}
              marking={markingIds.has(c.id)}
              onOpen={() => onOpenConversation(c.id)}
              onMarkRead={() => markRead(c.id)}
            />
          ))}
          {!loading && items.length === 0 && (
            <div className="flex flex-col items-center gap-2 text-center py-16 text-xs" style={{ color: INK_3 }}>
              <MessageCircle size={20} />
              Aucune notification
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default DiscussionNotifDrawer;
